/**
 * Which graphics card the pages ask the browser for (P2.9-T19, R-0166). On a machine with two, a browser gives a WebGL
 * context the integrated one unless the page asks otherwise, so the game and the editor ask for the faster one.
 * Phaser 4.2.1 reads `render.powerPreference` (`core/Config.js`) and passes it in the attributes of
 * `canvas.getContext("webgl", ...)` (`renderer/webgl/WebGLRenderer.js`); it has no WebGPU renderer. The value is a
 * hint: the system's per-application graphics setting, a screen cabled to the other card and a battery saver can each
 * overrule it, which is why the benchmark's report names the renderer that drew.
 */
export const GPU_POWER_PREFERENCE = "high-performance" satisfies WebGLPowerPreference;

/**
 * The `render` block every Phaser game a player or builder sees is created with.
 *
 * @example
 * new Phaser.Game({ type: Phaser.WEBGL, render: GPU_RENDER_CONFIG, scene: [scene] });
 */
export const GPU_RENDER_CONFIG = { powerPreference: GPU_POWER_PREFERENCE } as const;

/**
 * The `render` block the map editor's Phaser game is created with: `GPU_RENDER_CONFIG` without stencil buffers. The
 * editor draws no mask, so a stencil buffer on each dynamic texture (one per library page and per chunk) only costs the
 * allocation and validation of a renderbuffer of the texture's size when the texture is made, which cost about two seconds
 * of framebuffer validation over a load of Aethervale and a GPU stall in the frame after each.
 *
 * @example
 * new Phaser.Game({ type: Phaser.AUTO, render: EDITOR_RENDER_CONFIG, scene: [scene] });
 */
export const EDITOR_RENDER_CONFIG = { ...GPU_RENDER_CONFIG, stencil: false } as const;
